import { prepareDataCoordInfo, getStackedOnPoint } from 'echarts/lib/chart/line/helper';
import type SeriesData from 'echarts/types/src/data/SeriesData';
import type Cartesian2D from 'echarts/types/src/coord/cartesian/Cartesian2D';
import type Polar from 'echarts/types/src/coord/polar/Polar';
import { createFloat32Array } from 'echarts/lib/util/vendor';
import type { RankLineSeriesOption as LineSeriesOption } from './RankLineSeriesModel';

interface DiffItem {
  cmd: '+' | '=' | '-';
  idx: number;
  idx1?: number;
  isRemove?: boolean;
}

function diffData(oldData: SeriesData, newData: SeriesData) {
  const diffResult: DiffItem[] = [];

  newData
    .diff(oldData)
    .add(function (idx) {
      diffResult.push({ cmd: '+', idx });
    })
    .update(function (newIdx, oldIdx) {
      diffResult.push({ cmd: '=', idx: oldIdx, idx1: newIdx });
    })
    .remove(function (idx) {
      diffResult.push({ cmd: '+', idx, isRemove: true });
    })
    .execute();

  return diffResult;
}

export default function lineAnimationDiff(
  oldData: SeriesData,
  newData: SeriesData,
  oldStackedOnPoints: ArrayLike<number>,
  newStackedOnPoints: ArrayLike<number>,
  oldCoordSys: Cartesian2D | Polar,
  newCoordSys: Cartesian2D | Polar,
  oldValueOrigin: NonNullable<LineSeriesOption['areaStyle']>['origin'],
  newValueOrigin: NonNullable<LineSeriesOption['areaStyle']>['origin']
) {
  const diff = diffData(oldData, newData);

  // let newIdList = newData.mapArray(newData.getId);
  // let oldIdList = oldData.mapArray(oldData.getId);

  // convertToIntId(newIdList, oldIdList);

  // // FIXME One data ?
  // diff = arrayDiff(oldIdList, newIdList);

  const currPoints: number[] = [];
  const nextPoints: number[] = [];
  // Points for stacking base line
  const currStackedPoints: number[] = [];
  const nextStackedPoints: number[] = [];

  const status = [];
  const sortedIndices: number[] = [];
  const rawIndices: number[] = [];

  const newDataOldCoordInfo = prepareDataCoordInfo(oldCoordSys, newData, oldValueOrigin);
  // console.log('newDataOldCoordInfo', newDataOldCoordInfo);

  const oldDataNewCoordInfo = prepareDataCoordInfo(newCoordSys, oldData, newValueOrigin);

  const oldPoints = (oldData.getLayout('points') as number[]) || [];
  const newPoints = (newData.getLayout('points') as number[]) || [];

  for (let i = 0; i < diff.length; i++) {
    const diffItem = diff[i];
    let pointAdded = true;

    let oldIdx2: number;
    let newIdx2: number;

    let removePoints = 0;
    diff.forEach(item => {
      if (item.cmd === '+' && item.isRemove) {
        removePoints += 1;
      }
    });
    const needRemovePoint = removePoints <= 1;

    // FIXME, animation is not so perfect when dataZoom window moves fast
    // Which is in case remvoing or add more than one data in the tail or head
    switch (diffItem.cmd) {
      case '=':
        oldIdx2 = diffItem.idx * 2;
        newIdx2 = diffItem.idx1 * 2;
        let currentX = oldPoints[oldIdx2];
        let currentY = oldPoints[oldIdx2 + 1];
        const nextX = newPoints[newIdx2];
        const nextY = newPoints[newIdx2 + 1];

        // If previous data is NaN, use next point directly
        if (isNaN(currentX) || isNaN(currentY)) {
          currentX = nextX;
          currentY = nextY;
        }
        currPoints.push(currentX, currentY);
        nextPoints.push(nextX, nextY);

        currStackedPoints.push(oldStackedOnPoints[oldIdx2], oldStackedOnPoints[oldIdx2 + 1]);
        nextStackedPoints.push(newStackedOnPoints[newIdx2], newStackedOnPoints[newIdx2 + 1]);

        rawIndices.push(newData.getRawIndex(diffItem.idx1));
        break;
      case '+':
        const newIdx = diffItem.idx;
        const newDataDimsForPoint = newDataOldCoordInfo.dataDimsForPoint;
        const oldDataDimsForPoint = oldDataNewCoordInfo.dataDimsForPoint;
        newIdx2 = newIdx * 2;
        let oldPt = oldCoordSys.dataToPoint([
          newData.get(newDataDimsForPoint[0], newIdx),
          newData.get(newDataDimsForPoint[1], newIdx)
        ]);
        let newPt = [newPoints[newIdx2], newPoints[newIdx2 + 1]];

        if (diffItem.isRemove && needRemovePoint) {
          oldPt = [oldPoints[newIdx2], oldPoints[newIdx2 + 1]];
          newPt = newCoordSys.dataToPoint([
            oldData.get(oldDataDimsForPoint[0], newIdx),
            oldData.get(oldDataNewCoordInfo[1], newIdx)
          ]);
          newPt[1] = isNaN(newPt[1]) ? oldPt[1] : newPt[1];
          newPt[0] = isNaN(newPt[0]) ? oldPt[0] : newPt[0];
        }
        currPoints.push(oldPt[0], oldPt[1]);
        nextPoints.push(newPt[0], newPt[1]);

        const stackedOnPoint = getStackedOnPoint(newDataOldCoordInfo, oldCoordSys, newData, newIdx);

        currStackedPoints.push(stackedOnPoint[0], stackedOnPoint[1]);
        nextStackedPoints.push(newStackedOnPoints[newIdx2], newStackedOnPoints[newIdx2 + 1]);

        rawIndices.push(newData.getRawIndex(newIdx));
        break;
      case '-':
        pointAdded = false;
    }

    // Original indices
    if (pointAdded) {
      status.push(diffItem);
      sortedIndices.push(sortedIndices.length);
    }
  }

  // Diff result may be crossed if all items are changed
  // Sort by data index
  sortedIndices.sort(function (a, b) {
    return rawIndices[a] - rawIndices[b];
  });

  const len = currPoints.length;
  const sortedCurrPoints = createFloat32Array(len);
  const sortedNextPoints = createFloat32Array(len);

  const sortedCurrStackedPoints = createFloat32Array(len);
  const sortedNextStackedPoints = createFloat32Array(len);

  const sortedStatus = [];
  for (let i = 0; i < sortedIndices.length; i++) {
    const idx = sortedIndices[i];
    const i2 = i * 2;
    const idx2 = idx * 2;
    sortedCurrPoints[i2] = currPoints[idx2];
    sortedCurrPoints[i2 + 1] = currPoints[idx2 + 1];
    sortedNextPoints[i2] = nextPoints[idx2];
    sortedNextPoints[i2 + 1] = nextPoints[idx2 + 1];

    sortedCurrStackedPoints[i2] = currStackedPoints[idx2];
    sortedCurrStackedPoints[i2 + 1] = currStackedPoints[idx2 + 1];
    sortedNextStackedPoints[i2] = nextStackedPoints[idx2];
    sortedNextStackedPoints[i2 + 1] = nextStackedPoints[idx2 + 1];

    sortedStatus[i] = status[idx];
  }

  return {
    current: sortedCurrPoints,
    next: sortedNextPoints,

    stackedOnCurrent: sortedCurrStackedPoints,
    stackedOnNext: sortedNextStackedPoints,

    status: sortedStatus
  };
}
